import _ from 'lodash';
import moment from 'moment/moment';
import config from '../../../app/config';
import { mapObservationFromAPI, obsStatuses } from '../../../pages/ward/forms/observation/obsUtils';
import { DEFAULT_OBS_MAP } from '../../../pages/ward/map/patientList/utils/edReviewUtils';
import { findPatientFlagTitle } from '../../../pages/ward/mainScreen/util/patientFlagsUtil';
import { getConfig } from '../../../util/configUtil';

export const FILTER_OPERATORS = {
    EQUAL: 'Equal',
    NOT_EQUAL: 'NotEqual',
    STARTS_WITH: 'StartsWith',
    ENDS_WITH: 'EndsWith',
    CONTAINS: 'Contains',
    DOES_NOT_CONTAIN: 'DoesNotContain',
    CONTAINS_MULTIPLE: 'EqualMultiple',
    DOES_NOT_CONTAIN_MULTIPLE: 'NotEqualMultiple',
    DOES_NOT_START_WITH: 'DoesNotStartWith',
    DOES_NOT_END_WITH: 'DoesNotEndWith',
    NOT_EMPTY: 'NotEmpty',
    EMPTY: 'Empty',
    LIKE: 'Like',
    LESS_THAN: 'LessThan',
    LESS_THAN_OR_EQUAL: 'LessThanOrEqual',
    GREATER_THAN: 'GreaterThan',
    GREATER_THAN_OR_EQUAL: 'GreaterThanOrEqual',
    NULL: 'Null',
    NOT_NULL: 'NotNull',
};

export const getFilterRowsDataForColumn = (column, rows) => {
    const dtt = getFilterRowDataType(column.dataType);
    if (dtt === 'dict') {
        return _.uniq(
            rows.flatMap((row) => {
                return getFilterRowData(column.dataType, _.get(row, column.field), row, column, true);
            }),
        );
    }
    return _.sortBy(
        _.uniq(
            rows.map((row) => {
                return getFilterRowData(column.dataType, _.get(row, column.field), row, column, true);
            }),
        ),
    );
};
const getPatientName = (patient) => {
    return `${patient.familyName?.toUpperCase()} ${patient.firstName}`;
};

const getPtAge = (dob, rowData) => {
    if (dob) {
        const age = moment.duration(moment(rowData?.admission?.startDate).diff(moment(dob))).asYears();
        if (age < 1) {
            return 0;
        } else {
            return Math.floor(age);
        }
    }
    return undefined;
};
const getObservation = (obs, type, pt) => {
    if (!obs) return null;
    const mapped = mapObservationFromAPI(obs);
    const age = moment.duration(moment(pt?.admission?.startDate).diff(moment(pt?.patient?.dob))).asYears();
    const obsFiled = DEFAULT_OBS_MAP.find((obs) => obs.id === type);
    if (obsFiled) {
        if ((obsFiled.onlyPaed && age < 18) || !obsFiled.onlyPaed) {
            const valueObj = mapped[obsFiled.objFiled];
            const status = valueObj.status;
            let value;
            if (status === obsStatuses.notRecorded) {
                value = 'NR';
            } else if (status === obsStatuses.patientRefused) {
                value = 'PR';
            } else if (status === obsStatuses.equipment) {
                value = 'EQ';
            } else if (status === obsStatuses.patientCondition) {
                value = 'PC';
            } else if (status === obsStatuses.notTestable) {
                value = 'NT';
            } else if (status === obsStatuses.noReaction) {
                value = 'No reaction';
            } else {
                value = _.get(valueObj, obsFiled.valuePath);
            }
            if (_.isString(valueObj)) return _.startCase(valueObj);
            if (_.isBoolean(value)) return String(value);
            return value?.toString();
        }
    }
    return null;
};

const getPtNumbers = (pt) => {
    return `${pt?.hospitalNumber || '-'}, ${pt?.nhsNumber || '-'}`;
};

export const getFilterRowDataType = (type) => {
    switch (type) {
        case 'string':
        case 'priority':
        case 'ptName':
        case 'gender':
        case 'fullPatient':
        case 'fullPatientBars':
        case 'fullPatientNumbers':
        case 'observation':
        case 'onlyHour':
        case 'ecdsPreferredTerm':
        case 'bedRequestNew':
            return 'string';
        case 'boolean':
        case 'booleanOnlyTrue':
            return 'boolean';
        case 'age':
        case 'scoreBox':
            return 'number';
        case 'date':
        case 'onlyDate':
            return 'date';
        case 'flags':
        case 'clinician':
        case 'clinicalConclusionFull':
        case 'referrals':
        case 'invTreatDiag':
            return 'dict';
        default:
            console.error('not supported filter column type ' + type);
            return null;
    }
};

const getFilterRowData = (type, val, rowData, col, asString) => {
    const defaultVal = col?.defaultValue || '-';
    switch (type) {
        case 'string':
        case 'priority':
            return val || defaultVal;
        case 'boolean':
        case 'booleanOnlyTrue':
            return val;
        case 'ptName':
            return getPatientName(val);
        case 'gender':
            return rowData?.patient?.gender;
        case 'age':
            return getPtAge(val, rowData);
        case 'fullPatient':
            if (!rowData.__id) {
                return '';
            }
            return getPatientName(val);
        case 'fullPatientBars':
            return getPatientName(val);
        case 'fullPatientNumbers':
            if (!rowData.__id) {
                return '';
            }
            return getPtNumbers(val);
        case 'date':
            if (!val) return asString ? '-' : val;
            return asString ? moment(val).format(config.defaultDateTimeFormat) : moment(val);
        case 'onlyDate':
            if (!val) return asString ? '-' : val;
            return asString ? moment(val).format(config.defaultDateFormat) : moment(val);
        case 'onlyHour':
            if (!val) return asString ? '-' : val;
            return asString ? moment(val).format(config.defaultTimeFormat) : moment(val);
        case 'flags':
            return _.filter(val, (f) => f.checked).map((v) => findPatientFlagTitle(v.name));
        case 'scoreBox':
            return val; //number
        case 'observation':
            return getObservation(val, col.id, rowData);
        case 'clinicalConclusionFull':
        case 'clinician': {
            const consultant = val?.who?.__description ?? val?.__description;
            return consultant ?? null;
        }
        case 'ecdsPreferredTerm':
            return val?.snomedPreferredTerm || val?.__description;
        case 'referrals':
            if (_.isEmpty(val)) return null;
            return val.map((ref) => ref?.ReferredToService_SnomedCt?.__description);
        case 'invTreatDiag':
            if (_.isEmpty(val)) return [];
            const isDiag = col.id === 'diagnosis';
            return val.map((obj) => {
                if (isDiag) {
                    const field = obj?.value?.diagnosisMax ? 'snomedFullySpecifiedName' : 'snomedDescription';
                    return obj?.value?.[field];
                }
                return obj?.value?.__description;
            });

        case 'bedRequestNew':
            const oldBedRequest = getConfig('modules.forms.bedBooking.enableBedRequestForm', false);
            if (val) {
                if (_.isEmpty(val)) return null;
                let brStatus = 'Requested';

                if (oldBedRequest) {
                    if (val?.allocated) brStatus = 'Allocated';
                    if (val?.ready) brStatus = 'Ready';
                    if (val?.canceled) brStatus = 'Cancelled';
                } else {
                    if (val?.canceled?.checked) brStatus = 'Cancelled';
                    if (val?.allocated?.checked) brStatus = 'Allocated';
                }
                return brStatus;
            }
            return null;
        case 'total':
        case 'nextObs':
        case 'nextObsTimeLeft':
        case 'plans':
        case 'icd':
        case 'arrivalTime':
        case 'infections':
        case 'arrivalMode':
        case 'histComplaint':
        case 'chiefComplain':
        case 'safeguarding':
        case 'allObs':
        case 'referralStatus':
        case 'referralGroup':
        case 'taskDate':
        case 'taskName':
        case 'taskUser':
        case 'no':
        case 'clinicalConclusion':
        case 'patientReviewActions':
        case 'timeLeft':
        case 'timeLeftBars':
        case 'timeLeftAppointments':
        case 'edReviewStatus':
        case 'offWardType':
        case 'offWardDate':
        case 'location':
        case 'mergePatient':
        case 'editDemographics':
        default:
            console.error('not supported filter column type ' + type);
            return null;
    }
};

const isConditionTrueData = (condition, operator, value) => {
    let _value = value ? moment(value) : undefined;
    switch (operator) {
        case FILTER_OPERATORS.EQUAL:
            return moment(condition).isSame(_value);
        case FILTER_OPERATORS.NOT_EQUAL:
            return !moment(condition).isSame(_value);
        case FILTER_OPERATORS.LESS_THAN:
            return moment(condition).isAfter(_value);
        case FILTER_OPERATORS.LESS_THAN_OR_EQUAL:
            return moment(condition).isSameOrAfter(_value);
        case FILTER_OPERATORS.GREATER_THAN:
            return moment(condition).isBefore(_value);
        case FILTER_OPERATORS.GREATER_THAN_OR_EQUAL:
            return moment(condition).isSameOrBefore(_value);
        case FILTER_OPERATORS.NULL:
            return value === null || value === undefined;
        case FILTER_OPERATORS.NOT_NULL:
            return value !== null && value !== undefined;
        default:
            return true;
    }
};

const isConditionTrueNumber = (condition, operator, value) => {
    switch (operator) {
        case FILTER_OPERATORS.EQUAL:
            return !!value && value === condition;
        case FILTER_OPERATORS.NOT_EQUAL:
            return !!value && value !== condition;
        case FILTER_OPERATORS.LESS_THAN:
            return !!value && value < condition;
        case FILTER_OPERATORS.LESS_THAN_OR_EQUAL:
            return !!value && value <= condition;
        case FILTER_OPERATORS.GREATER_THAN:
            return !!value && value > condition;
        case FILTER_OPERATORS.GREATER_THAN_OR_EQUAL:
            return !!value && value >= condition;
        case FILTER_OPERATORS.NULL:
            return value === null || value === undefined;
        case FILTER_OPERATORS.NOT_NULL:
            return value !== null && value !== undefined;
        default:
            return true;
    }
};

function like(str, pattern, ignoreCase = true) {
    if (typeof str !== 'string' || typeof pattern !== 'string') return false;

    let regexPattern = pattern
        .replace(/([.+^${}()|[\]\\])/g, '\\$1')
        .replace(/%/g, '.*')
        .replace(/_/g, '.');

    const regex = new RegExp(`^${regexPattern}$`, ignoreCase ? 'i' : '');
    return regex.test(str);
}

const isConditionTrueString = (condition, operator, value, matchCase, valueArray) => {
    switch (operator) {
        case FILTER_OPERATORS.EQUAL:
            return matchCase ? value === condition : value?.toLowerCase() === condition?.toLowerCase();
        case FILTER_OPERATORS.NOT_EQUAL:
            return matchCase ? value !== condition : value?.toLowerCase() !== condition?.toLowerCase();
        case FILTER_OPERATORS.STARTS_WITH:
            return matchCase ? value?.startsWith(condition) : value?.toLowerCase()?.startsWith(condition?.toLowerCase());
        case FILTER_OPERATORS.ENDS_WITH:
            return matchCase ? value?.endsWith(condition) : value?.toLowerCase()?.endsWith(condition?.toLowerCase());
        case FILTER_OPERATORS.CONTAINS:
            return matchCase ? value?.includes(condition) : value?.toLowerCase()?.includes(condition?.toLowerCase());
        case FILTER_OPERATORS.CONTAINS_MULTIPLE:
            return matchCase
                ? valueArray?.some((v) => value?.includes(v))
                : valueArray?.some((v) => value?.toLowerCase()?.includes(v?.toLowerCase()));
        case FILTER_OPERATORS.DOES_NOT_CONTAIN:
            return matchCase ? !value?.includes(condition) : !value?.toLowerCase()?.includes(condition?.toLowerCase());
        case FILTER_OPERATORS.DOES_NOT_CONTAIN_MULTIPLE:
            return matchCase
                ? !valueArray?.some((v) => value?.includes(v))
                : !valueArray?.some((v) => value?.toLowerCase()?.includes(v?.toLowerCase()));
        case FILTER_OPERATORS.DOES_NOT_START_WITH:
            return matchCase ? !value?.startsWith(condition) : !value?.toLowerCase()?.startsWith(condition?.toLowerCase());
        case FILTER_OPERATORS.DOES_NOT_END_WITH:
            return matchCase ? !value?.endsWith(condition) : !value?.toLowerCase()?.endsWith(condition?.toLowerCase());
        case FILTER_OPERATORS.NOT_EMPTY:
            return value !== null && value !== undefined && value.length > 0;
        case FILTER_OPERATORS.EMPTY:
            return value === null || value === undefined || value.length === 0;
        case FILTER_OPERATORS.LIKE:
            return like(value, condition, !matchCase);
        default:
            return true;
    }
};

const filterRow = (row, columnsFilter) => {
    return _.every(Object.values(columnsFilter), (cf) => {
        const rd = getFilterRowData(cf.column.dataType, _.get(row, cf.column.field), row, cf.column, false);
        if (cf.filter.checked) {
            if (_.isArray(rd)) {
                return _.some(rd, (val) => _.includes(cf.filter.checked, val));
            }
            return _.includes(cf.filter.checked, rd);
        }
        if (cf.filter.filter && Object.values(cf.filter.filter).length > 0) {
            const f = cf.filter.filter;
            const emptyOrNot = f.operator1 === 'Empty' || f.operator1 === 'NotEmpty';
            const emptyOrNot2 = f.operator2 === 'Empty' || f.operator2 === 'NotEmpty';
            let isCT = false;
            if (f.value1 || !_.isEmpty(f.value1Array) || emptyOrNot) {
                if (moment.isMoment(f.value1)) {
                    isCT = isConditionTrueData(f.value1, f.operator1, rd);
                } else if (Number.isFinite(f.value1)) {
                    isCT = isConditionTrueNumber(f.value1, f.operator1, rd);
                } else {
                    isCT = isConditionTrueString(f.value1, f.operator1, rd, f.matchCase, f.value1Array);
                }
            }
            if (f.value2 || !_.isEmpty(f.value2Array) || emptyOrNot2) {
                if (!isCT && f.logic === 'AND') return false;
                if (isCT && f.logic === 'OR') return true;
                if (moment.isMoment(f.value2)) {
                    return isConditionTrueData(f.value2, f.operator2, rd);
                } else if (Number.isFinite(f.value2)) {
                    return isConditionTrueNumber(f.value2, f.operator2, rd);
                } else {
                    return isConditionTrueString(f.value2, f.operator2, rd, f.matchCase, f.value2Array);
                }
            }
            return isCT;
        }
        return true;
    });
};

export const filterData = (data, columnsFilters) => {
    if (Object.values(_.pickBy(columnsFilters, (cf) => !!cf))?.length === 0) {
        return data;
    }
    let clone = [...data];
    return _.filter(clone, (pt) => {
        if (
            filterRow(
                pt,
                _.pickBy(columnsFilters, (cf) => !!cf),
            )
        )
            return pt;
    });
};

export const getColumnsData = (data, columns) => {
    let columnData = {};
    _.forEach(columns, (column) => {
        if (column.filter) {
            columnData[column.id] = _.sortBy(_.filter(getFilterRowsDataForColumn(column, data), (v) => v !== null && v !== undefined));
        }
    });
    return columnData;
};

export const typeFiltersName = (type) => {
    switch (type) {
        case 'number':
            return 'Number Filters';
        case 'date':
            return 'Date Filters';
        case 'string':
            return 'String Filters';
        case 'dict':
            return 'Filters';
        default:
            return '';
    }
};

export const typeOperators = {
    string: [
        { name: '', menu: false, select: true },
        { name: 'Equal', menu: true, select: true },
        { name: 'Not Equal', menu: true, select: true },
        { name: 'divider', menu: true, select: false },
        { name: 'Equal Multiple', menu: true, select: true },
        { name: 'Not Equal Multiple', menu: true, select: true },
        { name: 'divider', menu: true, select: false },
        { name: 'Starts With', menu: true, select: true },
        { name: 'Ends With', menu: true, select: true },
        { name: 'divider', menu: true, select: false },
        { name: 'Contains', menu: true, select: true },
        { name: 'Does Not Contain', menu: true, select: true },
        { name: 'divider', menu: true, select: false },
        { name: 'Does Not Start With', menu: false, select: true },
        { name: 'Does Not End With', menu: false, select: true },
        { name: 'Not Empty', menu: false, select: true },
        { name: 'Like', menu: false, select: true },
    ],
    number: [
        { name: '', menu: false, select: true },
        { name: 'Equal', menu: true, select: true },
        { name: 'Not Equal', menu: true, select: true },
        { name: 'divider', menu: true, select: false },
        { name: 'Less Than', menu: true, select: true },
        { name: 'Less Than Or Equal', menu: true, select: true },
        { name: 'Greater Than', menu: true, select: true },
        { name: 'Greater Than Or Equal', menu: true, select: true },
        { name: 'Between', menu: true, select: false },
        { name: 'Null', menu: false, select: true },
        { name: 'Not Null', menu: false, select: true },
    ],
    date: [
        { name: '', menu: false, select: true },
        { name: 'Equal', menu: true, select: true },
        { name: 'Not Equal', menu: true, select: true },
        { name: 'divider', menu: true, select: false },
        { name: 'Less Than', menu: true, select: true },
        { name: 'Less Than Or Equal', menu: true, select: true },
        { name: 'Greater Than', menu: true, select: true },
        { name: 'Greater Than Or Equal', menu: true, select: true },
        { name: 'Between', menu: true, select: false },
        { name: 'Null', menu: false, select: true },
        { name: 'Not Null', menu: false, select: true },
    ],
    dict: [
        { name: 'Not Empty', menu: true, select: false, noModal: true },
        { name: 'Empty', menu: true, select: false, noModal: true },
    ],
};
